import React, { useRef, useEffect, useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import moment from "moment";
import { i18n } from "../../translate/i18n";
// FullCalendar v6+ - CSS é incluído automaticamente via os plugins

/**
 * Componente FullCalendar para substituir react-big-calendar
 * 
 * @param {Object} props
 * @param {Array} props.events - Array de eventos no formato FullCalendar
 * @param {string} props.currentView - View atual: 'month', 'week', 'day', 'agenda'
 * @param {Date} props.currentDate - Data atual do calendário
 * @param {string} props.userTimeZone - Timezone do usuário (ex: "America/Sao_Paulo", "America/Cuiaba")
 * @param {Function} props.onViewChange - Callback quando view muda
 * @param {Function} props.onDateChange - Callback quando data muda
 * @param {Function} props.onEventClick - Callback quando evento é clicado
 * @param {Function} props.onEditSchedule - Callback para editar agendamento (scheduleId)
 * @param {Function} props.onDeleteSchedule - Callback para excluir agendamento (scheduleId)
 * @param {Function} props.getCalendarApi - Callback para expor API do calendário
 */
const FullCalendarView = ({
  events = [],
  currentView = "month",
  currentDate = new Date(),
  userTimeZone = "America/Sao_Paulo", // Fallback para SP se não fornecido
  onViewChange,
  onDateChange,
  onEventClick,
  onEditSchedule,
  onDeleteSchedule,
  getCalendarApi,
}) => {
  const calendarRef = useRef(null);

  // Mapear view names do react-big-calendar para FullCalendar
  const viewMap = {
    month: "dayGridMonth",
    week: "timeGridWeek",
    day: "timeGridDay",
    agenda: "listWeek",
  };

  // Calcular data atual baseada na view
  const getTodayDateForView = (view) => {
    const today = new Date();
    
    if (view === "week" || view === "agenda") {
      // Para semana: mostrar início da semana atual (segunda-feira)
      // moment().startOf('week') usa domingo por padrão, então precisamos ajustar
      // Como configuramos firstDay={1} (segunda), precisamos usar isoWeek
      return moment(today).startOf('isoWeek').toDate();
    } else if (view === "day") {
      // Para dia: mostrar dia atual
      return today;
    } else {
      // Para mês: mostrar mês atual
      return today;
    }
  };

  // Expor API do calendário para o componente pai
  useEffect(() => {
    if (calendarRef.current && getCalendarApi) {
      const api = calendarRef.current.getApi();
      getCalendarApi(api);
    }
  }, [getCalendarApi]);
  
  // Na primeira montagem, garantir que está mostrando a data atual
  useEffect(() => {
    if (calendarRef.current) {
      const api = calendarRef.current.getApi();
      if (!api) return;
      
      // Sempre forçar navegação para data atual na primeira montagem
      const dateToShow = getTodayDateForView(currentView);
      
      // Usar setTimeout para garantir que o calendário está totalmente renderizado
      setTimeout(() => {
        api.gotoDate(dateToShow);
      }, 300);
    }
  }, []); // Executar apenas uma vez na montagem
  
  // Quando view mudar, navegar para a data atual da nova view
  useEffect(() => {
    if (calendarRef.current) {
      const api = calendarRef.current.getApi();
      if (!api) return;
      
      const fcView = viewMap[currentView] || "dayGridMonth";
      const dateToShow = getTodayDateForView(currentView);
      
      // Se a view mudou, mudar view E navegar para data atual
      if (api.view.type !== fcView) {
        // Mudar view primeiro
        api.changeView(fcView);
        // Depois navegar para a data atual (usar delay para garantir que view mudou)
        setTimeout(() => {
          api.gotoDate(dateToShow);
        }, 100);
      } else {
        // Se a view é a mesma, sempre forçar navegação para data atual ao abrir
        // Isso garante que ao abrir a página, sempre mostra a data atual
        setTimeout(() => {
          api.gotoDate(dateToShow);
        }, 50);
      }
    }
  }, [currentView]);


  // Atualizar data quando currentDate mudar externamente
  useEffect(() => {
    if (calendarRef.current) {
      const api = calendarRef.current.getApi();
      const currentDateObj = api.getDate();
      
      // Comparar apenas data (sem hora) para evitar loops
      if (
        currentDateObj.getFullYear() !== currentDate.getFullYear() ||
        currentDateObj.getMonth() !== currentDate.getMonth() ||
        currentDateObj.getDate() !== currentDate.getDate()
      ) {
        api.gotoDate(currentDate);
      }
    }
  }, [currentDate]);

  const handleDatesSet = (arg) => {
    if (onDateChange) {
      onDateChange(arg.view.currentStart);
    }
  };

  const handleViewChange = (arg) => {
    if (onViewChange) {
      // Mapear de volta para formato do react-big-calendar
      const viewMapReverse = {
        dayGridMonth: "month",
        timeGridWeek: "week",
        timeGridDay: "day",
        listWeek: "agenda",
        listDay: "agenda",
      };
      const rbView = viewMapReverse[arg.view.type] || "month";
      onViewChange(rbView);
    }
  };

  const handleEventClick = (clickInfo) => {
    if (onEventClick) {
      onEventClick(clickInfo.event);
    }
  };

  // Calcular data inicial baseada na view (usar useMemo para evitar recálculos)
  const initialDate = useMemo(() => {
    return getTodayDateForView(currentView);
  }, [currentView]);

  // ✅ Parte B: Render custom do evento com botões (eventContent)
  const renderEventContent = (eventInfo) => {
    const { event } = eventInfo;
    const { scheduleId, status, sentAt, canEdit } = event.extendedProps;
    
    // ✅ Parte C: Usar canEdit para decidir se mostra botões
    const showActions = canEdit === true && onEditSchedule && onDeleteSchedule;
    
    // Formatar hora do evento
    const timeStr = eventInfo.timeText || "";
    const title = event.title || "Sem nome";
    
    return (
      <div className="fc-event-content-wrapper" style={{ 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "space-between", 
        width: "100%", 
        padding: "2px 4px",
        position: "relative",
        zIndex: 1,
      }}>
        <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          {timeStr && <span style={{ marginRight: "4px", fontWeight: 500 }}>{timeStr}</span>}
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</span>
        </div>
        {showActions && (
          <div className="fc-event-actions" style={{ 
            display: "flex", 
            gap: "4px", 
            marginLeft: "4px", 
            flexShrink: 0,
            pointerEvents: "auto",
            zIndex: 10,
          }}>
            <button
              className="fc-event-edit-btn"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (onEditSchedule && canEdit) {
                  onEditSchedule(Number(scheduleId));
                }
              }}
              style={{
                background: "rgba(255, 255, 255, 0.9)",
                border: "none",
                borderRadius: "3px",
                padding: "2px 6px",
                cursor: "pointer",
                fontSize: "12px",
                color: "#1976d2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "24px",
                height: "20px",
                pointerEvents: "auto",
              }}
              title={i18n.t("common.buttons.edit")}
            >
              ✏️
            </button>
            <button
              className="fc-event-delete-btn"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (onDeleteSchedule && canEdit) {
                  onDeleteSchedule(Number(scheduleId));
                }
              }}
              style={{
                background: "rgba(255, 255, 255, 0.9)",
                border: "none",
                borderRadius: "3px",
                padding: "2px 6px",
                cursor: "pointer",
                fontSize: "12px",
                color: "#d32f2f",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "24px",
                height: "20px",
                pointerEvents: "auto",
              }}
              title={i18n.t("common.buttons.delete")}
            >
              🗑️
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <FullCalendar
      ref={calendarRef}
      plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
      initialView={viewMap[currentView] || "dayGridMonth"}
      initialDate={initialDate}
      events={events}
      headerToolbar={false}
      height="auto"
      locale="pt-br"
      timeZone={userTimeZone}
      firstDay={1} // Segunda-feira como primeiro dia da semana
      weekNumbers={false}
      navLinks={false}
      editable={false}
      selectable={false}
      dayMaxEvents={true}
      moreLinkClick="popover"
      eventClick={handleEventClick}
      datesSet={handleDatesSet}
      viewDidMount={handleViewChange}
      eventDisplay="block"
      eventContent={renderEventContent}
      eventTimeFormat={{
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }}
      slotMinTime="00:00:00"
      slotMaxTime="24:00:00"
      slotDuration="00:30:00"
      slotLabelInterval="01:00:00"
      allDaySlot={false}
      // Configurações específicas por view
      dayGridMonth={{
        eventLimit: 3,
        eventLimitClick: "popover",
        displayEventTime: true, // Mostrar horário nos eventos do Month
      }}
      timeGridWeek={{
        slotMinTime: "00:00:00",
        slotMaxTime: "24:00:00",
      }}
      timeGridDay={{
        slotMinTime: "00:00:00",
        slotMaxTime: "24:00:00",
      }}
      listWeek={{
        listDayFormat: { weekday: "long", month: "long", day: "numeric" },
        listDaySideFormat: false,
      }}
    />
  );
};

export default FullCalendarView;
